import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CoinTransaction {
  id: string
  type: 'earned' | 'spent' | 'bonus' | 'subscription'
  amount: number
  description: string
  timestamp: Date
  feature?: string
  planId?: string
}

export interface CoinBalance {
  total: number
  earned: number
  spent: number
  bonus: number
  subscription: number
}

interface RraasiCoinState {
  balance: CoinBalance
  transactions: CoinTransaction[]
  isLoading: boolean
  error: string | null
  
  // Actions
  addCoins: (amount: number, type: CoinTransaction['type'], description: string, feature?: string, planId?: string) => void
  spendCoins: (amount: number, feature: string) => boolean
  getBalance: () => number
  getTransactionHistory: () => CoinTransaction[]
  hasEnoughCoins: (amount: number) => boolean
  resetBalance: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  
  // Constants
  FREE_TIER_COINS: number
  DAILY_LOGIN_BONUS: number
  REFERRAL_BONUS: number
  SOCIAL_SHARE_BONUS: number
}

export const useRraasiCoinStore = create<RraasiCoinState>()(
  persist(
    (set, get) => ({
      balance: {
        total: 100, // Starting free coins
        earned: 100,
        spent: 0,
        bonus: 0,
        subscription: 0
      },
      transactions: [
        {
          id: 'welcome-bonus',
          type: 'bonus',
          amount: 100,
          description: 'Welcome bonus - Start your cosmic journey!',
          timestamp: new Date()
        }
      ],
      isLoading: false,
      error: null,
      
      // Constants
      FREE_TIER_COINS: 100,
      DAILY_LOGIN_BONUS: 5,
      REFERRAL_BONUS: 25,
      SOCIAL_SHARE_BONUS: 10,
      
      // Actions
      addCoins: (amount: number, type: CoinTransaction['type'], description: string, feature?: string, planId?: string) => {
        const transaction: CoinTransaction = {
          id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type,
          amount,
          description,
          timestamp: new Date(),
          feature,
          planId
        }
        
        set((state) => {
          const newBalance = { ...state.balance }
          
          switch (type) {
            case 'earned':
              newBalance.earned += amount
              break
            case 'bonus':
              newBalance.bonus += amount
              break
            case 'subscription':
              newBalance.subscription += amount
              break
          }
          
          newBalance.total = newBalance.earned + newBalance.bonus + newBalance.subscription - newBalance.spent
          
          return {
            balance: newBalance,
            transactions: [transaction, ...state.transactions]
          }
        })
      },
      
      spendCoins: (amount: number, feature: string): boolean => {
        const state = get()
        if (state.balance.total < amount) {
          return false
        }
        
        const transaction: CoinTransaction = {
          id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'spent',
          amount: -amount,
          description: `Used ${amount} coins for ${feature}`,
          timestamp: new Date(),
          feature
        }
        
        set((state) => ({
          balance: {
            ...state.balance,
            spent: state.balance.spent + amount,
            total: state.balance.total - amount
          },
          transactions: [transaction, ...state.transactions]
        }))
        
        return true
      },
      
      getBalance: () => get().balance.total,
      
      getTransactionHistory: () => get().transactions,
      
      hasEnoughCoins: (amount: number) => get().balance.total >= amount,
      
      resetBalance: () => {
        set({
          balance: {
            total: 100,
            earned: 100,
            spent: 0,
            bonus: 0,
            subscription: 0
          },
          transactions: [
            {
              id: 'welcome-bonus',
              type: 'bonus',
              amount: 100,
              description: 'Welcome bonus - Start your cosmic journey!',
              timestamp: new Date()
            }
          ]
        })
      },
      
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      
      setError: (error: string | null) => set({ error })
    }),
    {
      name: 'rraasi-coin-storage',
      partialize: (state) => ({
        balance: state.balance,
        transactions: state.transactions
      })
    }
  )
)
